import type { components } from '../../api/generated/schema'

type Schema = components['schemas']

/**
 * The typed section map for an encounter.
 *
 * Two of the three are hand-written; `cards` is the exception and worth noting, because
 * it is the one place in the whole client where the generated types reach *inside* a
 * section. `EncounterCardDto` is a real return type — `GET /encounters/{id}/cards/{characterId}`
 * produces one — so the card itself is generated, and only the list wrapper around it is
 * transcribed. That also means the card's fourteen character halves come free: they are
 * the Phase 2 section DTOs verbatim, which is what lets Stage 4's renderers draw a card
 * without a second set of types.
 */
export interface EncounterSections {
  core: EncounterCoreSection
  sides: EncounterSidesSection
  cards: EncounterCardsSection
}

/** Encounter identity plus the counts a list row needs without loading the sides. */
export interface EncounterCoreSection {
  id: number
  campaignId: number
  name: string
  type: Schema['EncounterType']
  notes: string
  sideCount: number
  participantCount: number
  createdAt: string
  /** Where the fight happens, or null if the Game Master has not said. */
  locationId: number | null
  locationName: string | null
  /** The location's battle map. A place holds at most one, so this is a URL, not a list. */
  battleMapImageUrl: string | null
  /**
   * Whether a live session could be started right now. Four rules folded server-side — Game
   * Master, a location, a battle map on it, and none already running — so no client re-derives
   * them to decide whether to draw a button.
   */
  canStartLiveSession: boolean
  /** The session already running for this encounter, if there is one. */
  liveSessionId: string | null
}

/** A character standing on one side, portrait already resolved. */
export interface EncounterSideCharacter {
  /** The join row's id. Removal, though, addresses the character id — see mutations.ts. */
  id: number
  characterId: number
  name: string
  type: Schema['CharacterType']
  portraitUrl: string | null
}

/** One side of the encounter and who is on it. */
export interface EncounterSide {
  id: number
  sideName: string
  characters: EncounterSideCharacter[]
}

/** The encounter's sides, in creation order. Section key `sides`. */
export interface EncounterSidesSection {
  sides: EncounterSide[]
}

/** A participant's full card — the character sheet through a combat lens. Generated. */
export type EncounterCard = Schema['EncounterCardDto']

/** Every participant's card. Section key `cards`, served only by the cards endpoint. */
export interface EncounterCardsSection {
  cards: EncounterCard[]
}

export type EncounterSummary = Schema['EncounterSummaryDto']
export type EncounterCandidate = Schema['EncounterCandidateDto']

/**
 * An encounter sheet.
 *
 * There is no `isGameMaster` here, and its absence is the access model: encounters are
 * Game Master-only for **reads** as well as writes, because a card is every NPC's full
 * sheet. Holding one of these at all means the caller may write it.
 */
export interface EncounterSheet {
  encounterId: number
  campaignId: number
  version: number
  sections: Partial<EncounterSections>
}

export function toEncounterSheet(dto: Schema['EncounterSheetDto']): EncounterSheet {
  if (dto.encounterId === undefined) {
    throw new Error('The API returned an encounter sheet with no id.')
  }

  return {
    encounterId: dto.encounterId,
    campaignId: dto.campaignId ?? 0,
    version: dto.version ?? 0,
    sections: (dto.sections ?? {}) as Partial<EncounterSections>,
  }
}
